import { Eraser, Hexagon } from "lucide-react";
import { useT } from "@/src/i18n";
import { useMapStore } from "@/src/store/useMapStore";
import type { TerrainBrush, TerrainId } from "@/src/types/map";

const terrainOptions: Array<{ id: TerrainId; color: string; mark: string }> = [
  { id: "plains", color: "#b8c991", mark: "╱╱" },
  { id: "forest", color: "#789267", mark: "▲" },
  { id: "hills", color: "#c9a86b", mark: "⌒" },
  { id: "mountain", color: "#98968d", mark: "△" },
  { id: "water", color: "#74a9bf", mark: "≈" },
  { id: "swamp", color: "#76866b", mark: "‡" },
  { id: "desert", color: "#dfc27b", mark: "◌" },
  { id: "snow", color: "#dce7e7", mark: "✣" },
  { id: "settlement", color: "#c89c78", mark: "⌂" },
];

export function TerrainDrawer() {
  const active = useMapStore((state) => state.selectedTerrain);
  const hexCount = useMapStore((state) => state.hexCells.length);
  const setActive = useMapStore((state) => state.setSelectedTerrain);
  const t = useT();
  const choose = (brush: TerrainBrush) => setActive(brush);

  return (
    <aside className="flex max-h-full w-64 flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#1c1b18]/97 shadow-2xl shadow-black/30 backdrop-blur">
      <div className="shrink-0 flex items-start gap-2 border-b border-white/8 px-4 py-3">
        <Hexagon size={15} className="mt-0.5 text-[#d75c51]" />
        <div>
          <p className="text-xs font-semibold text-stone-100">{t("terrain.title")}</p>
          <p className="mt-1 text-[10px] text-stone-500">{t("terrain.subtitle")}</p>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="p-3 pb-0">
        <button
          type="button"
          onClick={() => choose("erase")}
          className={`flex h-10 w-full items-center justify-center gap-2 rounded-xl border text-[10px] font-semibold transition ${active === "erase" ? "border-[#d45a50] bg-[#a8322c] text-white" : "border-red-400/20 bg-red-500/[0.05] text-red-300/80 hover:border-red-400/35 hover:bg-red-500/10 hover:text-red-200"}`}
          aria-pressed={active === "erase"}
        >
          <Eraser size={14} /> {active === "erase" ? t("terrain.eraseActive") : t("terrain.erase")} <span className="text-[8px] opacity-60">E</span>
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1.5 p-3">
        {terrainOptions.map((terrain) => {
          const selected = active === terrain.id;
          return (
            <button
              key={terrain.id}
              type="button"
              onClick={() => choose(terrain.id)}
              className={`flex min-h-17 flex-col items-center justify-center rounded-xl border p-1.5 text-center transition ${selected ? "border-[#c3463d] bg-[#a8322c]/25 text-white" : "border-white/6 bg-white/[0.025] text-stone-400 hover:border-white/15 hover:bg-white/[0.06] hover:text-stone-100"}`}
              aria-pressed={selected}
            >
              <span className="grid h-8 w-9 place-items-center rounded-lg border border-black/15 text-sm font-black text-[#3f4436]" style={{ backgroundColor: terrain.color }}>{terrain.mark}</span>
              <span className="mt-1 text-[9px] font-medium leading-none">{t(`terrain.${terrain.id}`)}</span>
            </button>
          );
        })}
      </div>
      </div>
      <div className="shrink-0 flex items-center justify-between border-t border-white/8 px-4 py-2.5 text-[9px] leading-4 text-stone-500"><span>{t("terrain.paintedHexes", { count: hexCount })}</span><span>{active === "erase" ? t("terrain.dragToRemove") : t("terrain.dragToPaint")}</span></div>
    </aside>
  );
}
